// third-party
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// project imports
import apiSlice from './apiSlice';
import { hasPermission, validateProjectAccess, getUserContext, createAuditEntry } from '../../utils/rbac';

// ----------------------------------------------------------------------

export const fetchDataHubProjects = createAsyncThunk('datahubProject/fetchProjects', async (_, { dispatch, rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();
        if (!hasPermission(user_permissions, 'projects', 'read')) {
            throw new Error('Insufficient permissions: Cannot read projects');
        }
        const data = await dispatch(
            apiSlice.endpoints.getDatahubProjects.initiate({ user_token, permissions: user_permissions, groups: user_groups })
        ).unwrap();
        const list = Array.isArray(data) ? data : (data?.data ?? data?.projects ?? []);
        const accessibleProjects = list.filter((project) => {
            try {
                return validateProjectAccess(user_permissions, user_groups, project);
            } catch (e) {
                console.warn(`Access denied to project ${project.id}:`, e.message);
                return false;
            }
        });
        createAuditEntry(user_id, 'fetch_projects', 'projects', 'all', true);
        return accessibleProjects;
    } catch (error) {
        const { user_id } = getUserContext();
        createAuditEntry(user_id, 'fetch_projects', 'projects', 'all', false, error.message);
        return rejectWithValue(error?.data || error.response?.data || error.message);
    }
});

export const createDataHubProject = createAsyncThunk('datahubProject/createProject', async (projectData, { dispatch, rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();
        if (!hasPermission(user_permissions, 'projects', 'create')) {
            throw new Error('Insufficient permissions: Cannot create projects');
        }
        const data = await dispatch(
            apiSlice.endpoints.createDatahubProject.initiate({
                ...projectData,
                user_token,
                permissions: user_permissions,
                groups: user_groups,
                audit: { action: 'create_project', resource_type: 'project', details: { project_name: projectData.name } }
            })
        ).unwrap();
        createAuditEntry(user_id, 'create_project', 'projects', data?.id ?? 'new', true);
        return data;
    } catch (error) {
        const { user_id } = getUserContext();
        createAuditEntry(user_id, 'create_project', 'projects', 'new', false, error.message);
        return rejectWithValue(error?.data || error.response?.data || error.message);
    }
});

export const updateDataHubProject = createAsyncThunk(
    'datahubProject/updateProject',
    async ({ projectId, projectData }, { dispatch, rejectWithValue }) => {
        try {
            const { user_token, user_permissions, user_groups, user_id } = getUserContext();
            if (!hasPermission(user_permissions, 'projects', 'update')) {
                throw new Error('Insufficient permissions: Cannot update projects');
            }
            const data = await dispatch(
                apiSlice.endpoints.updateDatahubProject.initiate({
                    projectId,
                    ...projectData,
                    user_token,
                    permissions: user_permissions,
                    groups: user_groups,
                    audit: { action: 'update_project', resource_type: 'project', details: { project_id: projectId } }
                })
            ).unwrap();
            createAuditEntry(user_id, 'update_project', 'projects', projectId, true);
            return data;
        } catch (error) {
            const { user_id } = getUserContext();
            createAuditEntry(user_id, 'update_project', 'projects', projectId, false, error.message);
            return rejectWithValue(error?.data || error.response?.data || error.message);
        }
    }
);

export const deleteDataHubProject = createAsyncThunk('datahubProject/deleteProject', async (projectId, { dispatch, rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();
        if (!hasPermission(user_permissions, 'projects', 'delete')) {
            throw new Error('Insufficient permissions: Cannot delete projects');
        }
        await dispatch(
            apiSlice.endpoints.deleteDatahubProject.initiate({
                projectId,
                user_token,
                permissions: user_permissions,
                groups: user_groups,
                audit: { action: 'delete_project', resource_type: 'project', details: { project_id: projectId } }
            })
        ).unwrap();
        createAuditEntry(user_id, 'delete_project', 'projects', projectId, true);
        return projectId;
    } catch (error) {
        const { user_id } = getUserContext();
        createAuditEntry(user_id, 'delete_project', 'projects', projectId, false, error.message);
        return rejectWithValue(error?.data || error.response?.data || error.message);
    }
});

export const fetchProjectStats = createAsyncThunk('datahubProject/fetchStats', async (_, { dispatch, rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();
        if (!hasPermission(user_permissions, 'projects', 'read')) {
            throw new Error('Insufficient permissions: Cannot read project statistics');
        }
        const data = await dispatch(
            apiSlice.endpoints.getDatahubProjectStats.initiate({ user_token, permissions: user_permissions, groups: user_groups })
        ).unwrap();
        createAuditEntry(user_id, 'fetch_project_stats', 'projects', 'stats', true);
        return data;
    } catch (error) {
        const { user_id } = getUserContext();
        createAuditEntry(user_id, 'fetch_project_stats', 'projects', 'stats', false, error.message);
        return rejectWithValue(error?.data || error.response?.data || error.message);
    }
});

// ----------------------------------------------------------------------

const initialState = {
    projects: [],
    currentProject: null,
    loading: false,
    error: null,
    stats: {
        totalProjects: 0,
        activeProjects: 0,
        totalStorage: 0,
        totalMembers: 0
    }
};

// ----------------------------------------------------------------------

const slice = createSlice({
    name: 'datahubProject',
    initialState,
    reducers: {
        setCurrentProject(state, action) {
            state.currentProject = action.payload;
        },
        clearError(state) {
            state.error = null;
        },
        updateProjectStats(state, action) {
            state.stats = { ...state.stats, ...action.payload };
        },
        clearProjects(state) {
            state.projects = [];
            state.currentProject = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // fetchDataHubProjects
            .addCase(fetchDataHubProjects.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDataHubProjects.fulfilled, (state, action) => {
                state.loading = false;
                state.projects = action.payload;
            })
            .addCase(fetchDataHubProjects.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // createDataHubProject
            .addCase(createDataHubProject.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createDataHubProject.fulfilled, (state, action) => {
                state.loading = false;
                state.projects.push(action.payload);
            })
            .addCase(createDataHubProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // updateDataHubProject
            .addCase(updateDataHubProject.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateDataHubProject.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.projects.findIndex((project) => project.id === action.payload.id);
                if (index !== -1) {
                    state.projects[index] = action.payload;
                }
                if (state.currentProject?.id === action.payload.id) {
                    state.currentProject = action.payload;
                }
            })
            .addCase(updateDataHubProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // deleteDataHubProject
            .addCase(deleteDataHubProject.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteDataHubProject.fulfilled, (state, action) => {
                state.loading = false;
                state.projects = state.projects.filter((project) => project.id !== action.payload);
                if (state.currentProject?.id === action.payload) {
                    state.currentProject = null;
                }
            })
            .addCase(deleteDataHubProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // fetchProjectStats
            .addCase(fetchProjectStats.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProjectStats.fulfilled, (state, action) => {
                state.loading = false;
                state.stats = action.payload;
            })
            .addCase(fetchProjectStats.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

// ----------------------------------------------------------------------

// Reducer
export default slice.reducer;

// Actions
export const { setCurrentProject, clearError, updateProjectStats, clearProjects } = slice.actions;
